<template>
  <div
    class="min-h-screen w-full flex justify-center items-start p-6 bg-white dark:bg-gray-900"
  >
    <div
      class="w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
    >
      <!-- Scrollable content area -->
      <div class="max-h-[calc(100vh-3rem)] overflow-y-auto px-6 py-12">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          Terms of Service
        </h1>

        <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
          <strong>Effective Date:</strong> March 2025
        </p>

        <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
          Welcome to HiSell Technonology. These Terms of Service outline the
          rules and regulations for using our platform. By accessing or using
          our services, you agree to comply with these terms.
        </p>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            1. Acceptance of Terms
          </h2>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
            By accessing or using HiSell Technology, you agree to be bound by
            these Terms of Service, our Privacy Policy, and any additional terms
            that apply to certain features.
          </p>
        </section>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            2. Services Provided
          </h2>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
            HiSell Technology provides an online platform that allows users to
            manage stores, analyze business data using AI, and integrate
            third-party services such as Facebook.
          </p>
        </section>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            3. User Accounts & Responsibilities
          </h2>
          <ul
            class="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 space-y-2 mb-6"
          >
            <li>
              You must provide accurate and complete information when creating
              an account.
            </li>
            <li>
              You are responsible for maintaining the confidentiality of your
              login credentials.
            </li>
            <li>
              You may not use our services for unlawful or unauthorized
              activities.
            </li>
          </ul>
        </section>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            4. Facebook Data Integration
          </h2>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
            HiSell Technology integrates with Facebook to provide additional
            features, such as:
          </p>
          <ul
            class="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 space-y-2 mb-6"
          >
            <li>
              <strong>Facebook Login:</strong> Allows users to sign in using
              their Facebook credentials.
            </li>
            <li>
              <strong>Facebook Pixel & API:</strong> Enables data collection for
              marketing, analytics, and performance tracking.
            </li>
          </ul>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
            By using these features, you acknowledge and agree that:
          </p>
          <ul
            class="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 space-y-2 mb-6"
          >
            <li>Facebook may collect data from your interactions.</li>
            <li>
              Your data may be processed in accordance with Facebook’s privacy
              policies.
            </li>
            <li>
              You can manage data collection through your Facebook account
              settings.
            </li>
          </ul>
        </section>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            5. AI-Powered Store Data Analysis
          </h2>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
            Our platform uses AI to analyze store data and enhance user
            experience. This includes:
          </p>
          <ul
            class="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 space-y-2 mb-6"
          >
            <li>
              <strong>Sales and inventory insights:</strong> AI helps predict
              demand and optimize stock.
            </li>
            <li>
              <strong>Personalized recommendations:</strong> Tailoring product
              suggestions based on customer behavior.
            </li>
            <li>
              <strong>Automated reports:</strong> AI generates business
              performance summaries.
            </li>
          </ul>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
            By using AI-driven features, you consent to:
          </p>
          <ul
            class="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 space-y-2 mb-6"
          >
            <li>The collection and analysis of store data.</li>
            <li>
              AI-based predictions and automated decisions for optimization.
            </li>
            <li>
              The right to opt-out of AI-driven insights by adjusting your
              account settings.
            </li>
          </ul>
        </section>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            6. Data Privacy & Security
          </h2>
          <ul
            class="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 space-y-2 mb-6"
          >
            <li>
              We implement industry-standard security measures to protect user
              data.
            </li>
            <li>
              We do not sell or share personal data with unauthorized third
              parties.
            </li>
            <li>
              You have the right to request data deletion or modifications.
            </li>
          </ul>
        </section>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            7. Restrictions & Prohibited Use
          </h2>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
            You may not:
          </p>
          <ul
            class="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 space-y-2 mb-6"
          >
            <li>Use the platform for illegal or fraudulent activities.</li>
            <li>
              Reverse-engineer or exploit our services without permission.
            </li>
            <li>Interfere with the normal operation of the platform.</li>
          </ul>
        </section>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            8. Modifications to Terms
          </h2>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-6">
            We reserve the right to update these Terms of Service at any time.
            Changes will be notified through our platform or via email.
          </p>
        </section>

        <section>
          <h2
            class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          >
            9. Contact Information
          </h2>
          <p class="text-lg text-gray-700 dark:text-gray-300">
            For any questions regarding these terms, contact us at:
          </p>
          <p class="text-lg text-gray-700 dark:text-gray-300 font-medium mt-2">
            📧 Email:
            <a
              href="mailto:support@tryhisell.com"
              class="text-blue-600 dark:text-blue-400 hover:underline"
            >
              support@tryhisell.com
            </a>
          </p>
        </section>

        <p class="text-sm text-gray-500 dark:text-gray-400 mt-8">
          Last updated: March 2025
        </p>
      </div>
    </div>
  </div>
</template>
